"use client";

import { Box, BoxProps, Stack, Typography, Skeleton } from "@mui/material";
import { ElementType, ReactNode } from "react";
import { SurfaceCard, SurfaceCardProps } from "./surface-card";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { translateRole } from "./utils/translation";
import { formatStatusLabel, getStatusBadgeColors } from "@/lib/ui/status-badge";


/**
 * DashboardSurface
 * The main container for a dashboard section.
 */
export function DashboardSurface({ children, ...props }: BoxProps) {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 'var(--tc-space-4)',
        ...props.sx 
      }} 
      {...props}
    >
      {children}
    </Box>
  );
}
/**
 * SectionSurface
 * A contained area within a dashboard, usually with a header and content.
 */
export function SectionSurface({ children, title, subtitle, actions, contained = true, loading = false, ...props }: { 
  children: ReactNode; 
  title?: string; 
  subtitle?: string;
  actions?: ReactNode;
  contained?: boolean;
  loading?: boolean;
} & BoxProps) {
  return (
    <Box sx={{ ...props.sx }} {...props}>
      {(title || subtitle || actions) && (
        <Stack 
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ 
            justifyContent: "space-between", 
            alignItems: { xs: "flex-start", sm: "flex-end" },
            mb: 2,
          }}
        >
          <Box>
            {title && (
              <Typography variant="h5" sx={{ fontWeight: 850, color: 'var(--tc-text)', lineHeight: 1.25 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" sx={{ color: 'var(--tc-text-muted)', mt: 0.5, fontWeight: 550, lineHeight: 1.45 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions && <Box>{actions}</Box>}
        </Stack>
      )}
      {contained ? (
        <SurfaceCard sx={{ p: 0, position: 'relative' }}>
          {loading && (
            <Box sx={{ 
              position: 'absolute', 
              inset: 0, 
              zIndex: 10, 
              bgcolor: 'var(--tc-surface)', 
              opacity: 0.8,
              backdropFilter: 'blur(2px)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              borderRadius: 'inherit'
            }}>
               <Stack spacing={2} sx={{ width: '100%', p: 4 }}>
                 <Skeleton variant="text" width="40%" height={32} />
                 <Skeleton variant="rounded" height={200} />
               </Stack>
            </Box>
          )}
          {children}
        </SurfaceCard>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Skeleton variant="rounded" height={100} />
              <Skeleton variant="rounded" height={200} />
            </Stack>
          )}
          {!loading && children}
        </Box>
      )}
    </Box>
  );
}

/**
 * ContentSurface
 * A general purpose surface for data or forms.
 */
export function ContentSurface({ children, title, subtitle, padded = true, loading = false, innerSx, ...props }: { 
  children?: ReactNode; 
  title?: string;
  subtitle?: string;
  padded?: boolean;
  loading?: boolean;
  innerSx?: any;
} & SurfaceCardProps) {
  return (
    <SurfaceCard padded={false} sx={{ position: 'relative', overflow: 'hidden', ...props.sx }} {...props}>
      {(title || subtitle) && (
        <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1.5, md: 2 }, bgcolor: 'var(--tc-surface)' }}>
          {title && <Typography variant="body2" sx={{ fontWeight: 850, color: 'var(--tc-text)', display: 'block', lineHeight: 1.35 }}>{title}</Typography>}
          {subtitle && <Typography variant="caption" sx={{ color: 'var(--tc-text-muted)', mt: 0.5, fontWeight: 550, display: 'block', lineHeight: 1.45 }}>{subtitle}</Typography>}
        </Box>
      )}
      {loading && (
        <Stack spacing={1} sx={{ p: padded ? 3 : 0 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      )}
      <Box sx={{ p: padded ? { xs: 2, md: 3 } : 0, pt: padded && (title || subtitle) ? 1 : undefined, display: loading ? 'none' : 'block', ...innerSx }}>
        {children}
      </Box>
    </SurfaceCard>
  );
}

/**
 * StatSurface
 * Specifically optimized for high-impact metrics.
 */
export function StatSurface({ children, ...props }: SurfaceCardProps) {
  return (
    <SurfaceCard 
      sx={{ 
        height: '100%', 
        transition: 'box-shadow var(--tc-transition-fast)',
        "&:hover": {
          boxShadow: 'var(--tc-shadow-md)',
        },
        ...props.sx 
      }} 
      {...props}
    >
      {children}
    </SurfaceCard>
  );
}

/**
 * StatusBadge
 * Enterprise-grade status indicator — delegates to lib/ui/status-badge for all color mapping.
 */
export function StatusBadge({ status, label, sx, ...props }: { status?: string, label?: string } & BoxProps) {
  const colors = getStatusBadgeColors(status);
  const displayLabel = formatStatusLabel(status, label);

  return (
    <Box
      component="span"
      role="status"
      aria-label={displayLabel}
      {...props}
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.25,
          borderRadius: '20px',
          fontSize: '0.75rem',
          fontWeight: 800,
          lineHeight: 1.5,
          letterSpacing: '0.01em',
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          whiteSpace: 'nowrap',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {displayLabel}
    </Box>
  );
}

/**
 * WorkflowBadge
 * Enterprise badge for roles or system categories.
 */
export function WorkflowBadge({ label, icon, sx, ...props }: { label: string, icon?: ReactNode } & BoxProps) {
  const normalized = label?.toLowerCase().trim();
  
  const getRoleColors = () => {
    switch (normalized) {
      case 'subscriber':
        // Purple theme for Super Admin (Subscriber)
        return {
          bg: 'rgba(139, 92, 246, 0.08)',
          text: '#8b5cf6',
          border: 'rgba(139, 92, 246, 0.2)',
          hoverBorder: 'rgba(139, 92, 246, 0.4)',
          hoverBg: 'rgba(139, 92, 246, 0.15)'
        };
      case 'administrator':
      case 'admin':
        // Blue/Info theme for Admin
        return {
          bg: 'rgba(14, 165, 233, 0.08)',
          text: '#0ea5e9',
          border: 'rgba(14, 165, 233, 0.2)',
          hoverBorder: 'rgba(14, 165, 233, 0.4)',
          hoverBg: 'rgba(14, 165, 233, 0.15)'
        };
      case 'member':
      case 'user':
        // Primary/Indigo theme for Contributor
        return {
          bg: 'var(--tc-primary-soft)',
          text: 'var(--tc-primary)',
          border: 'var(--tc-primary-border)',
          hoverBorder: 'var(--tc-primary-hover)',
          hoverBg: 'rgba(99, 102, 241, 0.15)'
        };
      default:
        // Default neutral theme
        return {
          bg: 'var(--tc-bg-muted)',
          text: 'var(--tc-text)',
          border: 'var(--tc-border)',
          hoverBorder: 'var(--tc-primary-border)',
          hoverBg: 'var(--tc-surface)'
        };
    }
  };

  const colors = getRoleColors();

  return (
    <Box
      component="span"
      {...props}
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 'var(--tc-radius-sm)',
          fontSize: '0.75rem',
          fontWeight: 700,
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          boxShadow: 'var(--tc-shadow-sm)',
          transition: 'all var(--tc-transition-fast)',
          '&:hover': {
            borderColor: colors.hoverBorder,
            bgcolor: colors.hoverBg,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {icon && <Box sx={{ display: 'flex', fontSize: '0.875rem', opacity: 0.7 }}>{icon}</Box>}
      {translateRole(label)}
    </Box>
  );
}

/**
 * Enterprise Workflow & Approval Components
 * (Phase 4: Workflow UX Modernization)
 */

export function WorkflowStepBadge({ label, status = 'pending', sx, ...props }: { 
  label: string; 
  status?: 'pending' | 'completed' | 'current' | 'error'
} & BoxProps) {
  const getColors = () => {
    switch (status) {
      case 'completed': return { bg: 'var(--tc-success-soft)', text: 'var(--tc-success)', border: 'var(--tc-success-soft)' };
      case 'current': return { bg: 'var(--tc-primary-soft)', text: 'var(--tc-primary)', border: 'var(--tc-primary)' };
      case 'error': return { bg: 'var(--tc-danger-soft)', text: 'var(--tc-danger)', border: 'var(--tc-danger-soft)' };
      default: return { bg: 'var(--tc-bg-muted)', text: 'var(--tc-text-muted)', border: 'var(--tc-border)' };
    }
  };

  const colors = getColors();

  return (
    <Box
      component="span"
      {...props}
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          borderRadius: 'var(--tc-radius-full)',
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0,
          bgcolor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {label}
    </Box>
  );
}

export function WorkflowTimeline({ steps }: { 
  steps: { label: string; date?: string; description?: string; status: 'completed' | 'pending' | 'error' }[] 
}) {
  return (
    <Stack spacing={0} sx={{ position: 'relative', pl: 2.5, pt: 0.5 }}>
      <Box sx={{ position: 'absolute', left: 5, top: 10, bottom: 20, width: 2, bgcolor: 'var(--tc-border-soft)' }} />
      {steps.map((step, idx) => (
        <Box key={idx} sx={{ pb: idx === steps.length - 1 ? 0 : 3.5, position: 'relative', transition: 'all 0.2s', '&:hover .timeline-dot': { transform: 'scale(1.25)' } }}>
          <Box 
            className="timeline-dot"
            sx={{ 
              position: 'absolute', 
              left: -24.5, 
              top: 5, 
              width: 9, 
              height: 9, 
              borderRadius: '50%', 
              bgcolor: step.status === 'completed' ? 'var(--tc-success)' : step.status === 'error' ? 'var(--tc-danger)' : 'var(--tc-surface)',
              border: `2px solid ${step.status === 'completed' ? 'var(--tc-success)' : step.status === 'error' ? 'var(--tc-danger)' : 'var(--tc-border)'}`,
              boxShadow: step.status === 'completed' ? '0 0 0 3px var(--tc-success-soft)' : step.status === 'error' ? '0 0 0 3px var(--tc-danger-soft)' : 'none',
              zIndex: 1,
              transition: 'all 0.2s ease-in-out'
            }} 
          />
          <Stack spacing={0.25} sx={{ pl: 0.5 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: step.status === 'pending' ? 'var(--tc-text-muted)' : 'var(--tc-text)', letterSpacing: 0 }}>
                {step.label}
              </Typography>
              {step.date && <Typography variant="caption" sx={{ color: 'var(--tc-text-muted)', fontFamily: 'monospace', fontWeight: 600 }}>{step.date}</Typography>}
            </Stack>
            {step.description && <Typography variant="caption" sx={{ color: 'var(--tc-text-muted)', lineHeight: 1.4, display: 'block', maxWidth: '95%' }}>{step.description}</Typography>}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function getHistoryDate(date?: string) {
  if (!date) return "Not recorded";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsed).replace(",", " •");
}

function getHistoryGroupLabel(date?: string) {
  if (!date) return "Unscheduled";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "Unscheduled";

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed).toUpperCase();
}

function groupHistorySteps(steps: AccountHistoryStep[]) {
  return steps.reduce<{ label: string; items: AccountHistoryStep[] }[]>((groups, step) => {
    const label = getHistoryGroupLabel(step.date);
    const group = groups.find((item) => item.label === label);

    if (group) {
      group.items.push(step);
    } else {
      groups.push({ label, items: [step] });
    }

    return groups;
  }, []);
}

type AccountHistoryStep = {
  label: string;
  date?: string;
  description?: string;
  status: 'completed' | 'pending' | 'error';
  meta?: string;
};

export function AccountHistoryFeed({ steps }: { steps: AccountHistoryStep[] }) {
  if (steps.length === 0) {
    return (
      <PremiumEmptyState
        title="No account history"
        description="Lifecycle events will appear here once this subscriber has recorded activity."
        icon={HistoryRoundedIcon}
        sx={{ minHeight: 220, py: 4, background: 'var(--tc-bg-muted-soft)', borderRadius: 'var(--tc-radius-md)' }}
      />
    );
  }

  const groups = groupHistorySteps(steps);

  return (
    <Stack spacing={4} sx={{ p: 1 }}>
      {groups.map((group) => (
        <Stack key={group.label} spacing={2}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            "&::after": {
              content: '""',
              flexGrow: 1,
              height: '1px',
              bgcolor: 'var(--tc-border-soft)',
              opacity: 0.5
            }
          }}>
            <Typography variant="caption" sx={{ 
              color: 'var(--tc-text-muted)', 
              fontSize: '0.65rem', 
              fontWeight: 900, 
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}>
              {group.label}
            </Typography>
          </Box>
          <Stack
            spacing={3}
            sx={{
              position: 'relative',
              pl: 3.5,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 6,
                top: 8,
                bottom: 8,
                width: 2,
                bgcolor: 'var(--tc-border-soft)',
                borderRadius: 1
              },
            }}
          >
            {group.items.map((step, idx) => (
              <Box key={`${group.label}-${step.label}-${idx}`} sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: -24.5,
                    top: 6,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: step.status === 'completed' ? 'var(--tc-success)' : step.status === 'error' ? 'var(--tc-danger)' : 'var(--tc-bg)',
                    border: `2px solid ${step.status === 'completed' ? 'var(--tc-success)' : step.status === 'error' ? 'var(--tc-danger)' : 'var(--tc-border)'}`,
                    boxShadow: `0 0 0 4px ${step.status === 'completed' ? 'var(--tc-success-soft)' : step.status === 'error' ? 'var(--tc-danger-soft)' : 'var(--tc-bg-muted-soft)'}`,
                    zIndex: 1,
                    transition: 'all 0.2s ease'
                  }}
                />
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 850, color: 'var(--tc-text)', lineHeight: 1.3, pt: 0.25 }}>
                      {step.label}
                    </Typography>
                    <StatusBadge
                      status={step.status === 'completed' ? 'completed' : step.status}
                      label={step.status === 'completed' ? 'Verified' : formatStatusLabel(step.status)}
                      sx={{ flexShrink: 0, px: 1, py: 0, fontSize: '0.6rem', border: 0, height: 20, minWidth: 60, justifyContent: 'center' }}
                    />
                  </Stack>

                  {step.description && (
                    <Typography variant="caption" sx={{ color: 'var(--tc-text-muted)', lineHeight: 1.5, fontWeight: 550, display: 'block' }}>
                      {step.description}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ 
                      color: 'var(--tc-text-muted)', 
                      fontWeight: 750, 
                      fontSize: '0.65rem',
                      fontFamily: 'var(--tc-font-mono)',
                      opacity: 0.8
                    }}>
                      {getHistoryDate(step.date).split(' •')[1]}
                    </Typography>
                    {step.meta && (
                      <WorkflowBadge 
                        label={step.meta} 
                        sx={{ px: 0.75, py: 0.1, fontSize: '0.6rem', height: 18, bgcolor: 'var(--tc-primary-soft)', color: 'var(--tc-primary)', border: 0 }} 
                      />
                    )}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}



/**
 * PremiumEmptyState
 * A high-impact empty state component for production-grade SaaS.
 */
export function PremiumEmptyState({ title, description, icon: Icon, action, ...props }: {
  title: string;
  description: string;
  icon?: ElementType;
  action?: ReactNode;
} & BoxProps) {
  return (
    <Box 
      sx={{ 
        py: { xs: 4, md: 5 }, 
        px: 4, 
        width: '100%',
        minHeight: 240,
        height: '100%',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, var(--tc-bg-muted) 0%, var(--tc-surface) 100%)',
        ...props.sx 
      }} 
      {...props}
    >
      <Box sx={{ 
        mb: 2, 
        width: 44,
        height: 44,
        borderRadius: 'var(--tc-radius-md)', 
        background: 'linear-gradient(135deg, var(--tc-surface) 0%, var(--tc-bg-muted) 100%)',
        color: 'var(--tc-text-muted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--tc-border)',
        boxShadow: 'var(--tc-shadow-sm), inset 0 1px 0 var(--tc-surface-highlight)',
        transition: 'all var(--tc-transition-standard)',
        "&:hover": {
          color: 'var(--tc-primary)',
          borderColor: 'var(--tc-primary-border)',
          boxShadow: 'var(--tc-shadow-md), inset 0 1px 0 var(--tc-surface-highlight)',
          transform: 'translateY(-1px)'
        }
      }}>
        {Icon ? <Icon sx={{ fontSize: 22 }} /> : <InfoOutlinedIcon sx={{ fontSize: 22 }} />}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: 'var(--tc-text)' }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'var(--tc-text-muted)', maxWidth: 360, mb: action ? 2 : 0, lineHeight: 1.5, textAlign: 'center', display: 'block' }}>
        {description}
      </Typography>
      {action && <Box>{action}</Box>}
    </Box>
  );
}
